import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Dashboard as DashboardIcon,
  QuestionAnswer as QuestionIcon,
  Lightbulb as IdeaIcon,
  Forum as ForumIcon,
  Settings as ImplSettingsIcon,
  Home as ImplHomeIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircle as PlayCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';

export default function ImplementationHome({ currentPhase, onPhaseChange }) {
  const navigate = useNavigate();
  const [courseDispatches, setCourseDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  // 獲取課程派發列表
  const fetchCourseDispatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(config.getCourseDispatches);
      
      if (response.data.success) {
        setCourseDispatches(response.data.data || []);
      } else {
        setError('獲取課程列表失敗');
      }
    } catch (error) {
      console.error('獲取課程派發錯誤:', error);
      setError('獲取課程列表失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseDispatches();
  }, []);

  // 過濾課程
  const filteredCourses = courseDispatches.filter(course => {
    const searchLower = searchTerm.toLowerCase();
    return course.title.toLowerCase().includes(searchLower) ||
           (course.Class && course.Class.name.toLowerCase().includes(searchLower)) ||
           (course.LessonPackage && course.LessonPackage.name.toLowerCase().includes(searchLower));
  });

  // 獲取狀態顏色
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'completed': return 'default';
      case 'paused': return 'warning';
      default: return 'default';
    }
  };

  // 獲取狀態文字
  const getStatusText = (status) => {
    switch (status) {
      case 'active': return '進行中';
      case 'completed': return '已完成';
      case 'paused': return '已暫停';
      default: return '未知';
    }
  };

  // 處理選單開啟
  const handleMenuOpen = (event, courseId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCourseId(courseId);
  };

  // 處理選單關閉
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCourseId(null);
  };

  // 處理課程狀態變更
  const handleStatusChange = async (newStatus) => {
    if (selectedCourseId) {
      try {
        const response = await axiosInstance.put(`${config.updateCourseDispatchStatus}${selectedCourseId}/status`, {
          status: newStatus
        });
        if (response.data.success) {
          const statusText = newStatus === 'active' ? '進行中' : newStatus === 'completed' ? '已完成' : '已暫停';
          setSnackbar({ open: true, message: `課程狀態已更新為：${statusText}`, severity: 'success' });
          fetchCourseDispatches(); // 重新載入資料
        } else {
          setSnackbar({ open: true, message: '狀態更新失敗', severity: 'error' });
        }
      } catch (error) {
        console.error('更新課程狀態錯誤:', error);
        setSnackbar({ open: true, message: '狀態更新失敗', severity: 'error' });
      }
    }
    handleMenuClose();
  };

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('implementation', 'home', navigate);

  return (
    <div className="teacher-layout implementation-home-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="implementation"
          onPhaseChange={(phase) => {
            if (onPhaseChange) {
              onPhaseChange(phase);
            } else {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }
          }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">課中實施 - 課程列表</h1>
        </div>
        
        {/* 篩選區域在標題下方靠右 */}
        <div className="filter-container">
          <TextField
            placeholder="🔍 搜尋課程標題、社群或備課包..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="filter-search-input"
            sx={{ width: '300px' }}
          />
        </div>

        {/* 載入中 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* 無課程時的簡單提示 */}
        {!loading && !error && filteredCourses.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              目前尚未找到課程
            </Typography>
            <Typography variant="body2" color="text.secondary">
              請確認是否已在課前準備完成學生、備課包、社群與課程派發。
            </Typography>
          </Box>
        )}

        {/* 課程卡片列表 */}
        {!loading && !error && filteredCourses.length > 0 && (
          <div className="activity-card-list grid-responsive">
            {filteredCourses.map((course) => (
              <div
                className="activity-card"
                key={course.id}
                onClick={() => {
                  const url = `/teacher/implementation/community-applications/${course.id}/task-dispatch`;
                  window.open(url, '_blank');
                }}
              style={{ 
                position: 'relative', 
                background:'#fff', 
                boxShadow:'0 2px 8px rgba(44,44,80,0.08)', 
                borderRadius: '12px', 
                border:'1.5px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                minHeight: '200px',
                maxHeight: '200px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '16px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(44,44,80,0.12)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(44,44,80,0.08)';
              }}
            >
                    {/* 課程標題 */}
                    <div className="activity-name" style={{ 
                      fontSize: '1.2rem', 
                      marginBottom: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      minHeight: '2.8rem',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}>
                      {course.title}
                    </div>

                    {/* 學年學期與統計標籤 */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '6px', 
                      marginBottom: '12px',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {/* 學年學期標籤 */}
                      <Chip 
                        label={`${course.Class?.year || '?'} ${course.Class?.semester || '?'}`}
                        size="small"
                        sx={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: '500',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                      
                      {/* 統計標籤 */}
                      {course.stats && (
                        <>
                          <span style={{
                            backgroundColor: '#e8f5e8',
                            color: '#2e7d32',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}>
                            <PeopleIcon sx={{ fontSize: 12, mr: 0.25 }} />
                            {course.stats.totalStudents} 人
                          </span>
                          <span style={{
                            backgroundColor: '#f3e5f5',
                            color: '#7b1fa2',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}>
                            <AssignmentIcon sx={{ fontSize: 12, mr: 0.25 }} />
                            {course.stats.totalTasks} 任務
                          </span>
                          <span style={{
                            backgroundColor: '#fff3e0',
                            color: '#e65100',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: '500',
                            display: 'inline-flex',
                            alignItems: 'center'
                          }}>
                            <GroupIcon sx={{ fontSize: 12, mr: 0.25 }} />
                            {course.stats.totalGroups} 組
                          </span>
                        </>
                      )}
                    </div>

                    {/* 進入課程按鈕 */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '8px'
                    }}>
                      <button
                        className="btn-card"
                        onClick={(e) => {
                          e.stopPropagation();
                          const url = `/teacher/implementation/community-applications/${course.id}/task-dispatch`;
                          window.open(url, '_blank');
                        }}
                        style={{
                          minWidth: '120px'
                        }}
                      >
                        進入課程
                      </button>
                    </div>

                    {/* 更多選單按鈕 */}
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, course.id)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#666',
                        bgcolor: 'rgba(255,255,255,0.9)',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.05)'
                        },
                        zIndex: 1
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
              </div>
            ))}
          </div>
        )}

        {/* 更多選單 - 暫時註解課程狀態選單項 */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* 暫時註解課程狀態選單項 */}
          {/* <MenuItem onClick={() => handleStatusChange('active')}>
            <PlayCircleIcon sx={{ mr: 1, color: '#4caf50' }} />
            設為進行中
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('paused')}>
            <ScheduleIcon sx={{ mr: 1, color: '#ff9800' }} />
            設為已暫停
          </MenuItem>
          <MenuItem onClick={() => handleStatusChange('completed')}>
            <CheckCircleIcon sx={{ mr: 1, color: '#9e9e9e' }} />
            設為已完成
          </MenuItem> */}
        </Menu>

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}
